import { lazy, ReactElement, Suspense, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { DotLegalFullScreenSpinner, DotLegalAlert } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../../localization/useTranslation";
import { useDocumentPreview } from "./DocumentPreview.hooks";
import { useDocumentPreviewStyles } from "./DocumentPreview.styles";
import { DocumentFileType, ExtractedTextResponse, IDocumentPreviewToolbarAction, PreviewableDocument } from "./DocumentPreview.types";
import PdfViewer from "./PdfViewer";
import ImageViewer from "./ImageViewer";
import WordViewer from "./WordViewer";
import TextView from "./textView/TextView";
import { supportsExtractedText, useExtractedText } from "./textView/TextView.hooks";
import { ITextSelectionConfig } from "./textSelection/textSelection.types";
import { resolveSectionFromMarkdown } from "./textSelection/markdownSection";

// Shared so the "still loading" render does not hand TextView a new array identity each time, which its
// effects treat as a changed document.
const NO_PAGES: Array<string> = [];

// The Outlook parser and its encoding tables are only ever needed by a .msg preview, so
// they stay out of the bundle every other document type has to load.
const EmailViewer = lazy(() => import("./EmailViewer"));

export interface IDocumentPreviewContentProps {
    open: boolean;
    documents: Array<PreviewableDocument>;
    initialDocumentId?: string;
    initialPage?: number;
    initialSearchText?: string;
    initialMatchSnippet?: string;
    citationChunkContent?: string;
    /**
     * Shown above the viewer when the citation was not found. A caller that omits it takes responsibility
     * for telling the user itself - the previewer has no wording of its own to fall back on.
     */
    citationNotFoundText?: string;
    /**
     * Keeps a scanned pdf showing as the scan instead of its indexed text extraction. Off by default,
     * because a scan carries no text: nothing in it can be searched, highlighted or picked, so showing the
     * extraction is what makes the document usable at all. Only a caller for whom the document's
     * appearance is the point opts out.
     *
     * Deliberately a prop rather than something inferred from initialSearchText, citationChunkContent or
     * textSelection: the latter two arrive after the scan finishes, so inferring it would switch the viewer
     * out from under the user mid-review.
     */
    keepScannedAppearance?: boolean;
    /**
     * The document's text when the caller already has it and the document has no indexed version to fetch
     * it from, such as a file uploaded but not yet stored. Takes the place of the extracted-text fetch.
     */
    localExtractedText?: ExtractedTextResponse;
    textSelection?: ITextSelectionConfig;
    toolbarAction?: IDocumentPreviewToolbarAction;
    onDocumentNameChange?: (name: string) => void;
    onCurrentDocumentChange?: (document: PreviewableDocument | undefined) => void;
}

export default function DocumentPreviewContent(props: IDocumentPreviewContentProps) {
    const { translateString } = useTranslation();
    const styles = useDocumentPreviewStyles();

    const getDocumentKey = (doc: PreviewableDocument) => doc.documentVersionId ?? doc.documentId;

    const initialDocumentIndex = useMemo(() => {
        if (props.initialDocumentId) {
            const index = props.documents.findIndex((doc) => getDocumentKey(doc) === props.initialDocumentId);
            return index >= 0 ? index : 0;
        }
        return 0;
    }, [props.documents, props.initialDocumentId]);

    const [currentDocumentIndex, setCurrentDocumentIndex] = useState(initialDocumentIndex);
    const [activeInitialPage, setActiveInitialPage] = useState<number | undefined>(props.initialPage);
    const [activeInitialSearchText, setActiveInitialSearchText] = useState<string | undefined>(props.initialSearchText);
    const [activeInitialMatchSnippet, setActiveInitialMatchSnippet] = useState<string | undefined>(props.initialMatchSnippet);
    const [activeCitationChunkContent, setActiveCitationChunkContent] = useState<string | undefined>(props.citationChunkContent);
    const [pdfHasTextLayer, setPdfHasTextLayer] = useState<boolean | undefined>(undefined);
    const [citationFound, setCitationFound] = useState<boolean | undefined>(undefined);

    useEffect(() => {
        setCurrentDocumentIndex(initialDocumentIndex);
        setActiveInitialPage(props.initialPage);
        setActiveInitialSearchText(props.initialSearchText);
        setActiveInitialMatchSnippet(props.initialMatchSnippet);
        setActiveCitationChunkContent(props.citationChunkContent);
        setCitationFound(undefined);
    }, [initialDocumentIndex, props.initialPage, props.initialSearchText, props.initialMatchSnippet, props.citationChunkContent]);

    // Whether the pdf carries a text layer is a fact about the document, not about the citation, and answering
    // it again costs a reload and reparse of the whole file. Kept out of the effect above so that paging to
    // another obligation suggestion - which changes citationChunkContent - does not discard the answer and swap
    // the text view back out for the scan it replaced. Paging to another document resets it via resetActiveHints.
    useEffect(() => {
        setPdfHasTextLayer(undefined);
    }, [initialDocumentIndex]);

    const currentDocument = props.documents[currentDocumentIndex];

    const { onDocumentNameChange, onCurrentDocumentChange } = props;
    useEffect(() => {
        if (onDocumentNameChange) {
            onDocumentNameChange(currentDocument?.name ?? "");
        }
    }, [currentDocument?.name, onDocumentNameChange]);

    useEffect(() => {
        if (onCurrentDocumentChange) {
            onCurrentDocumentChange(currentDocument);
        }
    }, [currentDocument, onCurrentDocumentChange]);

    const {
        documentData,
        isLoading,
        error,
        fileType,
        zoomLevel,
        zoomInputValue,
        isDownloading,
        handleZoomIn,
        handleZoomBy,
        handleZoomOut,
        handleZoomInputChange,
        handleZoomInputKeyDown,
        handleZoomInputBlur,
        handleDownload,
    } = useDocumentPreview({ open: props.open, currentDocument });

    // A scanned PDF has pages but no text on them, so it gets the text extraction instead unless the caller
    // asked for the scan itself. Other unsupported types keep the download-only branch below.
    const hasExtractedTextSource = supportsExtractedText(currentDocument) || props.localExtractedText !== undefined;
    const showTextView = props.keepScannedAppearance !== true && fileType === DocumentFileType.Pdf && pdfHasTextLayer === false && hasExtractedTextSource;

    // The pdf.js text layer is positioned spans with no heading structure, so a selection's section is
    // read from the document's extracted markdown instead. Only fetched while picking text, since an
    // ordinary preview never needs it.
    const needsSectionLookup = fileType === DocumentFileType.Pdf && props.textSelection !== undefined && supportsExtractedText(currentDocument);

    const {
        pages: extractedTextPages,
        content: extractedText,
        pageOffsets,
        isLoading: isLoadingExtractedText,
        hasNoExtractedText,
    } = useExtractedText(currentDocument, showTextView || needsSectionLookup, props.localExtractedText);

    const resolveSection = (clause: string, pageNumber: number | undefined) =>
        extractedText ? resolveSectionFromMarkdown(extractedText, pageOffsets, clause, pageNumber) : undefined;

    const resetActiveHints = () => {
        setActiveInitialPage(undefined);
        setActiveInitialSearchText(undefined);
        setActiveInitialMatchSnippet(undefined);
        setActiveCitationChunkContent(undefined);
        setPdfHasTextLayer(undefined);
        setCitationFound(undefined);
    };

    const handlePreviousDocument = useCallback(() => {
        const newIndex = currentDocumentIndex > 0 ? currentDocumentIndex - 1 : props.documents.length - 1;
        setCurrentDocumentIndex(newIndex);
        resetActiveHints();
    }, [currentDocumentIndex, props.documents.length]);

    const handleNextDocument = useCallback(() => {
        const newIndex = currentDocumentIndex < props.documents.length - 1 ? currentDocumentIndex + 1 : 0;
        setCurrentDocumentIndex(newIndex);
        resetActiveHints();
    }, [currentDocumentIndex, props.documents.length]);

    const documentNavigation = useMemo(
        () => ({
            currentIndex: currentDocumentIndex,
            totalDocuments: props.documents.length,
            onPreviousDocument: handlePreviousDocument,
            onNextDocument: handleNextDocument,
        }),
        [currentDocumentIndex, props.documents.length, handlePreviousDocument, handleNextDocument]
    );

    if (error) {
        return (
            <Box sx={styles.errorContainer}>
                <Typography color="error">{translateString("failedToLoadDocument")}</Typography>
            </Box>
        );
    }

    const citationNotFoundText = citationFound === false ? props.citationNotFoundText : undefined;
    const selectTextHint = props.textSelection?.hint;

    // The wrapper is unconditional: a notice appearing later must not change the element type at this
    // position, or React remounts the viewer and the document is parsed and rendered all over again.
    const withNotices = (viewer: ReactElement) => {
        return (
            <Box sx={styles.noticedViewerContainer}>
                {selectTextHint && <DotLegalAlert severity="info" warningText={selectTextHint} marginTop={0} marginBottom={0} />}
                {citationNotFoundText && <DotLegalAlert severity="warning" warningText={citationNotFoundText} marginTop={0} marginBottom={0} />}
                <Box sx={styles.noticedViewer}>{viewer}</Box>
            </Box>
        );
    };

    if (showTextView) {
        if (hasNoExtractedText) {
            return (
                <Box sx={styles.errorContainer}>
                    <Typography color="text.secondary">{translateString("noSelectableTextInDocument")}</Typography>
                </Box>
            );
        }

        return withNotices(
            <TextView
                pages={extractedTextPages ?? NO_PAGES}
                isLoading={isLoadingExtractedText}
                zoomLevel={zoomLevel}
                zoomInputValue={zoomInputValue}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onZoomInputChange={handleZoomInputChange}
                onZoomInputKeyDown={handleZoomInputKeyDown}
                onZoomInputBlur={handleZoomInputBlur}
                onZoomBy={handleZoomBy}
                onDownload={handleDownload}
                isDownloading={isDownloading}
                documentNavigation={documentNavigation}
                toolbarAction={props.toolbarAction}
                citationChunkContent={activeCitationChunkContent}
                onCitationResolved={setCitationFound}
                textSelection={props.textSelection}
            />
        );
    }

    if (fileType === DocumentFileType.Pdf) {
        return withNotices(
            <PdfViewer
                blobUrl={documentData?.blobUrl ?? undefined}
                onDownload={handleDownload}
                isDownloading={isDownloading}
                zoomLevel={zoomLevel}
                zoomInputValue={zoomInputValue}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onZoomInputChange={handleZoomInputChange}
                onZoomInputKeyDown={handleZoomInputKeyDown}
                onZoomInputBlur={handleZoomInputBlur}
                onZoomBy={handleZoomBy}
                isOpen={props.open}
                isLoading={isLoading}
                documentNavigation={documentNavigation}
                toolbarAction={props.toolbarAction}
                initialPage={activeInitialPage}
                initialSearchText={activeInitialSearchText}
                citationChunkContent={activeCitationChunkContent}
                onCitationResolved={setCitationFound}
                onTextLayerDetected={setPdfHasTextLayer}
                textSelection={props.textSelection}
                resolveSection={needsSectionLookup ? resolveSection : undefined}
            />
        );
    }

    if (fileType === DocumentFileType.Image) {
        return (
            <ImageViewer
                imageUrl={documentData?.blobUrl}
                fileName={documentData?.fileName ?? currentDocument?.fileName ?? ""}
                zoomLevel={zoomLevel}
                zoomInputValue={zoomInputValue}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onZoomInputChange={handleZoomInputChange}
                onZoomInputKeyDown={handleZoomInputKeyDown}
                onZoomInputBlur={handleZoomInputBlur}
                onZoomBy={handleZoomBy}
                onDownload={handleDownload}
                isDownloading={isDownloading}
                isLoading={isLoading}
                documentNavigation={documentNavigation}
                toolbarAction={props.toolbarAction}
            />
        );
    }

    if (fileType === DocumentFileType.Word) {
        return withNotices(
            <WordViewer
                documentData={documentData?.arrayBuffer ?? undefined}
                isLoading={isLoading}
                zoomLevel={zoomLevel}
                zoomInputValue={zoomInputValue}
                onZoomIn={handleZoomIn}
                onZoomOut={handleZoomOut}
                onZoomInputChange={handleZoomInputChange}
                onZoomInputKeyDown={handleZoomInputKeyDown}
                onZoomInputBlur={handleZoomInputBlur}
                onZoomBy={handleZoomBy}
                onDownload={handleDownload}
                isDownloading={isDownloading}
                documentNavigation={documentNavigation}
                toolbarAction={props.toolbarAction}
                initialPage={activeInitialPage}
                initialSearchText={activeInitialSearchText}
                initialMatchSnippet={activeInitialMatchSnippet}
                citationChunkContent={activeCitationChunkContent}
                onCitationResolved={setCitationFound}
                textSelection={props.textSelection}
            />
        );
    }

    if (fileType === DocumentFileType.Email) {
        return (
            <Suspense fallback={<DotLegalFullScreenSpinner />}>
                <EmailViewer
                    // Remounting per document drops the reader's decision to load that message's
                    // remote images, so the next one starts blocked again.
                    key={currentDocument ? getDocumentKey(currentDocument) : ""}
                    documentData={documentData?.arrayBuffer ?? undefined}
                    fileName={documentData?.fileName ?? currentDocument?.fileName ?? ""}
                    documentKey={currentDocument ? getDocumentKey(currentDocument) : ""}
                    isLoading={isLoading}
                    zoomLevel={zoomLevel}
                    zoomInputValue={zoomInputValue}
                    onZoomIn={handleZoomIn}
                    onZoomOut={handleZoomOut}
                    onZoomInputChange={handleZoomInputChange}
                    onZoomInputKeyDown={handleZoomInputKeyDown}
                    onZoomInputBlur={handleZoomInputBlur}
                    onDownload={handleDownload}
                    isDownloading={isDownloading}
                    documentNavigation={documentNavigation}
                />
            </Suspense>
        );
    }

    return (
        <Box sx={styles.errorContainer}>
            <Typography color="text.secondary">{translateString("unsupportedFileType")}</Typography>
        </Box>
    );
}
